import React, { Dispatch, useContext, useEffect, useMemo, useRef } from "react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { flatten, isEqual, isUndefined } from "lodash";
import { FilterDescription } from "common/utils/queryBuilder";
import { styled } from "../../../../../stitches.config";
import { useOutsideAlerter } from "../../../../../utils/outsideAlerter";
import { Button } from "../../../Button";
import Divider from "../../../Divider";
import { EmptyState } from "../../../EmptyState";
import { TableContext } from "../../TableContext";
import { Attribute, isAttributeNode } from "../../types";
import {
  DropdownContentStyles,
  DropdownItemList,
  DropdownItemStyles,
  SectionTitleLabel,
} from "../__styles__/TableSettings";
import { AttributeRow, SectionTitleNavigation } from "../utils";
import { FilterCountWrapper } from "./__styles__/Filter";
import FilterRows from "./FilterRows";
import { FilterInput } from "./Inputs";
import {
  FilterAction,
  FilterReducerState,
  useFilterReducer,
} from "./useFilterReducer";
import {
  buildFilterDescriptionFromFilterStateObject,
  getAllFilterableAttributes,
} from "./utils";

const AppliedFiltersState = ({
  filterCount,
  filterState,
  dispatch,
}: {
  filterCount: number;
  filterState: FilterReducerState;
  dispatch: Dispatch<FilterAction>;
}) => {
  return (
    <>
      {filterCount === 0 ?
        <SectionTitleLabel>No filter applied</SectionTitleLabel>
      : <FilterRows filterState={filterState} dispatch={dispatch} />}
      <Divider extension={8} />
      <DropdownItem
        onSelect={event => {
          event.preventDefault();
          dispatch({ type: "addingFilter" });
        }}
      >
        <AttributeRow attribute={{ label: "Add filter", icon: "plus" }} />
      </DropdownItem>
    </>
  );
};

const AddFilterState = ({
  attributeHistory,
  currentAttribute,
  dispatch,
}: {
  attributeHistory: Array<Attribute>;
  currentAttribute: Attribute;
  dispatch: Dispatch<FilterAction>;
}) => {
  const parentAttributeLabel =
    attributeHistory[attributeHistory.length - 1]?.label;
  const hasConditionalParent = !isUndefined(currentAttribute.conditional);

  let menuTitle = parentAttributeLabel ?? "Filters";

  if (hasConditionalParent) {
    menuTitle = "Conditionals";
  }

  return (
    <>
      <Dropdown.Item
        onSelect={event => {
          event.preventDefault();
          dispatch({ type: "goBack" });
        }}
      >
        <SectionTitleNavigation label={menuTitle} />
      </Dropdown.Item>
      <Divider extension={8} />
      {isAttributeNode(currentAttribute) ?
        <>
          <SectionTitleLabel>Attributes</SectionTitleLabel>
          <DropdownItemList>
            {currentAttribute.attributes
              .filter(attribute => !attribute.omitFromFilters)
              .map((attribute, index) => (
                <DropdownItem
                  key={index}
                  onSelect={event => {
                    event.preventDefault();
                    dispatch({
                      type: "setAttribute",
                      data: {
                        newAttribute: attribute,
                        lastAttribute: currentAttribute,
                      },
                    });
                  }}
                >
                  <AttributeRow
                    attribute={attribute}
                    rightIcon={{ icon: "chevron-right" }}
                  />
                </DropdownItem>
              ))}
            {!currentAttribute.attributes.filter(
              attribute => !attribute.omitFromFilters,
            ).length && (
              <EmptyState
                title="No attributes"
                paddingTop={8}
                paddingBottom={8}
                compact
              />
            )}
          </DropdownItemList>
        </>
      : <FilterInput attribute={currentAttribute} dispatch={dispatch} />}
    </>
  );
};

export const Filter = ({
  config,
  onFilterChange,
  addingNewView,
  filterExclusionMatcher,
}: {
  config: Array<Attribute>;
  onFilterChange: (filters: Array<FilterDescription>) => void;
  addingNewView?: boolean;
  filterExclusionMatcher?: (filterDescription: FilterDescription) => boolean;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { currentQuery } = useContext(TableContext);

  const filterConfig = useMemo(
    () => getAllFilterableAttributes(config),
    [config],
  );

  const wrapperRef = useRef(null as null | HTMLDivElement);

  const [filterState, dispatch] = useFilterReducer({
    queryDescription: currentQuery!,
    initialAttributes: filterConfig,
    filterExclusionMatcher,
  });

  useEffect(() => {
    if (
      currentQuery &&
      !isEqual(filterState.originalQueryDescription, currentQuery)
    ) {
      dispatch({
        type: "buildNewFilterFromQuery",
        data: {
          queryDescription: currentQuery,
        },
      });
    }
  }, [currentQuery, dispatch, filterState.originalQueryDescription]);

  const { currentAttribute, attributeHistory, menuOpen, addingFilter } =
    filterState;

  const filterCount = flatten(Object.values(filterState.filterValues)).length;

  const toggleMenu = () => {
    if (menuOpen) {
      dispatch({ type: "closeMenu" });
    } else {
      dispatch({ type: "openMenu" });
    }
  };

  const handleReturnFocusToButton = (event: Event) => {
    event.preventDefault();
    buttonRef?.current?.focus();
  };

  useOutsideAlerter({
    ref: wrapperRef,
    onOutsideInteraction: () => {
      dispatch({ type: "closeMenu" });
    },
  });

  useEffect(() => {
    const filterDescriptions: Array<FilterDescription> = [];

    for (const [key, filters] of Object.entries(filterState.filterValues)) {
      for (const filter of filters) {
        filterDescriptions.push(
          buildFilterDescriptionFromFilterStateObject({
            field: key,
            filter,
          }),
        );
      }
    }

    onFilterChange(filterDescriptions);
  }, [JSON.stringify(filterState.filterValues)]);

  useEffect(() => {
    if (addingNewView) {
      dispatch({ type: "resetFilters" });
    }
  }, [addingNewView]);

  return (
    <div style={{ userSelect: "none", zIndex: 10 }}>
      <Button
        ref={buttonRef}
        onClick={toggleMenu}
        leftIconName="filter"
        styleVariant="secondary"
        size="small"
      >
        Filter
        {filterCount > 0 && (
          <FilterCountWrapper data-testid="filter-count">
            {filterCount}
          </FilterCountWrapper>
        )}
      </Button>

      <Dropdown.Root open={menuOpen} onOpenChange={toggleMenu}>
        <Dropdown.Trigger asChild>
          <div />
        </Dropdown.Trigger>
        <DropdownContent
          align="start"
          sideOffset={4}
          onCloseAutoFocus={handleReturnFocusToButton}
        >
          {addingFilter ?
            <AddFilterState
              currentAttribute={currentAttribute}
              attributeHistory={attributeHistory}
              dispatch={dispatch}
            />
          : <AppliedFiltersState
              filterCount={filterCount}
              dispatch={dispatch}
              filterState={filterState}
            />
          }
        </DropdownContent>
      </Dropdown.Root>
    </div>
  );
};

const DropdownContent = styled(Dropdown.Content, {
  ...DropdownContentStyles,
});

const DropdownItem = styled(Dropdown.Item, {
  ...DropdownItemStyles,
});
